// SearchList — the open tab's list on the Search page (pen Q82): one row per
// item, at most {results} a page, then the total in words and Show more.
//
// A row: the kind's icon, the title (the field's words marked on a neutral
// ground, never a colour; one line, the full title on hover), the AI mark
// where the assistant made it, and a meta line. Materials: "Kind · [folder]
// Project · changed …", the project a link that opens it. Sources: "Type ·
// used in N projects / Not used yet / Being read… / Could not be read ·
// added …". A click anywhere else opens the item; on hover the row tints
// and its "…" holds its actions (Rename, Archive, Delete…; archived:
// Unarchive).
//
// Every case: still loading (placeholder rows); nothing made yet (the
// tab's empty state); no match (says what was searched, one way out: remove
// the last pill, else clear the search); archived items out of the list
// until "Show archived (N)"; a long list paged by Show more.
//
// Pure: rows come in; a rename being typed is view-ephemeral local state;
// every choice reports up.

export type { Pill };
export { SearchList };

import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import type { Library } from "../../app/ports/workspace.js";
import { AiMarker } from "../AiMarker/AiMarker.js";
import { Button } from "../Button/Button.js";
import { EmptyState } from "../EmptyState/EmptyState.js";
import { Icon, type IconName } from "../Icon/Icon.js";
import { fileKindIcon, useFileKindLabel } from "../KnowledgeBank/fileKind.js";
import type { MenuItem } from "../Menu/Menu.js";
import { RowOverflow } from "../RowOverflow/RowOverflow.js";
import { RowRename } from "../RowOverflow/RowRename.js";
import {
    intentOfAction,
    type LibraryRowIntent,
    refOfResult,
    rowActionsFor,
} from "../Search/libraryRowIntent.js";
import type { SearchResult } from "../Search/searchModel.js";
import { Skeleton } from "../Skeleton/Skeleton.js";
import {
    archivedCount,
    type FieldQuery,
    type MaterialRow,
    matchSegments,
    RESULTS_PAGE,
    type SearchTab,
    type SourceRow,
    shownMaterials,
    shownSources,
} from "./searchRows.js";
import { materialKindIcon, useSearchWords } from "./searchWords.js";

// A chosen filter, shown as a pill in the field.
type Pill = {
    readonly key: string;
    readonly icon: IconName;
    readonly label: string;
    // "" for a pill that is its label alone (Archived).
    readonly value: string;
    readonly onRemove: () => void;
};

const SKELETON_ROWS = 4;

// A short date ("28 Oct"), in the viewer's language; nothing when unknown.
const useShortDate = (): ((iso: string | undefined) => string) => {
    const { i18n } = useLingui();
    const format = new Intl.DateTimeFormat(i18n.locale, {
        day: "numeric",
        month: "short",
    });
    return (iso) => {
        if (iso === undefined) return "";
        const at = new Date(iso);
        return Number.isNaN(at.getTime()) ? "" : format.format(at);
    };
};

// The row's "…" (the library's one lifecycle) and its title, which a
// rename swaps for a field.
const useRowActions = (onRowIntent: (intent: LibraryRowIntent) => void) => {
    const { t } = useLingui();
    const [renaming, setRenaming] = useState<string | null>(null);
    const item = (
        action: ReturnType<typeof rowActionsFor>[number],
    ): { readonly label: string; readonly icon: IconName } => {
        switch (action) {
            case "rename":
                return { label: t`Rename`, icon: "pencil" };
            case "archive":
                return { label: t`Archive`, icon: "archive" };
            case "unarchive":
                return { label: t`Unarchive`, icon: "undo" };
            case "delete":
                return { label: t`Delete…`, icon: "trash" };
        }
    };
    const menu = (result: SearchResult): ReactNode => {
        const ref = refOfResult(result);
        if (ref === null || renaming === result.id) return null;
        const items: readonly MenuItem[] = rowActionsFor(result).map(
            (action): MenuItem => ({
                _tag: "item",
                label: item(action).label,
                icon: <Icon name={item(action).icon} />,
                tone: action === "delete" ? "danger" : "default",
                onSelect: () =>
                    action === "rename"
                        ? setRenaming(result.id)
                        : onRowIntent(intentOfAction(action, ref)),
            }),
        );
        return (
            <RowOverflow
                label={t`Actions for ${result.title}`}
                items={items}
                className="bilda-search__more"
                menuClassName="bilda-search-menu"
            />
        );
    };
    const title = (
        result: SearchResult,
        words: readonly string[],
        onOpen: () => void,
    ): ReactNode => {
        const ref = refOfResult(result);
        return renaming === result.id && ref !== null ? (
            <RowRename
                initial={result.title}
                label={t`New name for ${result.title}`}
                onSubmit={(next) => {
                    setRenaming(null);
                    onRowIntent({ type: "rename-resource", ref, title: next });
                }}
                onCancel={() => setRenaming(null)}
                className="bilda-search__rename"
            />
        ) : (
            <button
                type="button"
                className="bilda-search__open"
                title={result.title}
                onClick={onOpen}
            >
                {matchSegments(result.title, words).map((seg, i) =>
                    seg.matched ? (
                        // biome-ignore lint/suspicious/noArrayIndexKey: runs of one title, in order
                        <mark key={i} className="bilda-search__match">
                            {seg.text}
                        </mark>
                    ) : (
                        // biome-ignore lint/suspicious/noArrayIndexKey: runs of one title, in order
                        <span key={i}>{seg.text}</span>
                    ),
                )}
            </button>
        );
    };
    return { menu, title };
};

const Dot = (): ReactNode => (
    <span className="bilda-search__dot" aria-hidden="true">
        ·
    </span>
);

// The meta line's last part, when it is known.
const When = ({ text }: { readonly text: string }): ReactNode =>
    text === "" ? null : (
        <>
            <Dot />
            <span>{text}</span>
        </>
    );

// One row: icon, title line, meta line, "…".
const Row = ({
    icon,
    title,
    ai,
    meta,
    menu,
}: {
    readonly icon: IconName;
    readonly title: ReactNode;
    readonly ai: boolean;
    readonly meta: ReactNode;
    readonly menu: ReactNode;
}): ReactNode => (
    <li className="bilda-search__item bilda-row-overflow-host">
        <span className="bilda-search__item-icon" aria-hidden="true">
            <Icon name={icon} size="md" />
        </span>
        <div className="bilda-search__item-text">
            <div className="bilda-search__item-title">
                {title}
                {ai ? <AiMarker /> : null}
            </div>
            <div className="bilda-search__meta">{meta}</div>
        </div>
        {menu}
    </li>
);

type SearchListProps = {
    readonly tab: SearchTab;
    readonly field: FieldQuery;
    readonly words: readonly string[];
    readonly library: Library | null;
    // The open tab's rows for the field, sorted; and every row (for the
    // empty state and "Show archived").
    readonly materials: readonly MaterialRow[];
    readonly sources: readonly SourceRow[];
    readonly materialRows: readonly MaterialRow[];
    readonly sourceRows: readonly SourceRow[];
    readonly pills: readonly Pill[];
    readonly pages: number;
    readonly onShowMore: () => void;
    readonly onShowArchived: () => void;
    readonly onClear: () => void;
    readonly onOpen: (result: SearchResult) => void;
    readonly onOpenProject: (projectId: string) => void;
    readonly onAddSource: () => void;
    readonly onNewProject: (() => void) | null;
    readonly onRowIntent: (intent: LibraryRowIntent) => void;
};

const SearchList = ({
    tab,
    field,
    words,
    library,
    materials,
    sources,
    materialRows,
    sourceRows,
    pills,
    pages,
    onShowMore,
    onShowArchived,
    onClear,
    onOpen,
    onOpenProject,
    onAddSource,
    onNewProject,
    onRowIntent,
}: SearchListProps): ReactNode => {
    const { t } = useLingui();
    const said = useSearchWords();
    const fileKind = useFileKindLabel();
    const date = useShortDate();
    const actions = useRowActions(onRowIntent);
    const added = (day: string): string => (day === "" ? "" : t`added ${day}`);

    // Still being read: placeholder rows in the real shape.
    if (library === null)
        return (
            <ul className="bilda-search__list" aria-busy="true">
                {Array.from({ length: SKELETON_ROWS }, (_, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: placeholders, no identity
                    <li key={i} className="bilda-search__item">
                        <div className="bilda-search__item-text">
                            <Skeleton width={`${60 - i * 8}%`} />
                            <Skeleton width="30%" />
                        </div>
                    </li>
                ))}
            </ul>
        );

    // Nothing made or brought in yet (archived ones count: they exist).
    const nothingYet =
        tab === "materials"
            ? materialRows.length === 0
            : sourceRows.length === 0;
    if (nothingYet)
        return (
            <div className="bilda-search__list" data-empty="true">
                {tab === "materials" ? (
                    <EmptyState
                        icon={<Icon name="folder-open" size="lg" />}
                        title={t`No materials yet`}
                        body={t`Materials are made inside a project. Start one, and what you make in it shows up here.`}
                    >
                        {onNewProject === null ? null : (
                            <Button
                                variant="primary"
                                size="lg"
                                onClick={onNewProject}
                            >
                                <Icon name="plus" size="sm" />
                                {t`New project`}
                            </Button>
                        )}
                    </EmptyState>
                ) : (
                    <EmptyState
                        icon={<Icon name="file-up" size="lg" />}
                        title={t`No sources yet. Add one to make material from it.`}
                    >
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={onAddSource}
                        >
                            {t`Add a source`}
                        </Button>
                    </EmptyState>
                )}
            </div>
        );

    const rows = tab === "materials" ? materials.length : sources.length;
    const archived =
        tab === "materials"
            ? archivedCount(materialRows, field, (r, q) =>
                  shownMaterials(r, q, { field: "name", order: "ascending" }),
              )
            : archivedCount(sourceRows, field, (r, q) =>
                  shownSources(r, q, { field: "name", order: "ascending" }),
              );
    const showArchived =
        archived === 0 ? null : (
            <button
                type="button"
                className="bilda-search__archived"
                onClick={onShowArchived}
            >
                {t`Show archived (${archived})`}
            </button>
        );

    // No match: what was searched, and one way out.
    if (rows === 0) {
        const last = pills.at(-1);
        const pillName = `${last?.label ?? ""} ${last?.value ?? ""}`.trim();
        const text = field.text.trim();
        return (
            <div className="bilda-search__list" data-empty="true">
                <EmptyState
                    icon={<Icon name="no-match" size="lg" />}
                    title={
                        text === ""
                            ? t`Nothing matches the filter`
                            : said.noMatch(tab, field.kind, text)
                    }
                >
                    <Button
                        variant="secondary"
                        size="md"
                        onClick={last === undefined ? onClear : last.onRemove}
                    >
                        <Icon name="close" size="sm" />
                        {last === undefined
                            ? t`Clear the search`
                            : t`Remove ${pillName}`}
                    </Button>
                </EmptyState>
                {showArchived}
            </div>
        );
    }

    const shownCount = Math.min(rows, Math.max(1, pages) * RESULTS_PAGE);
    const items =
        tab === "materials"
            ? materials.slice(0, shownCount).map((row) => (
                  <Row
                      key={row.result.id}
                      icon={materialKindIcon(row.kind)}
                      title={actions.title(row.result, words, () =>
                          onOpen(row.result),
                      )}
                      ai={row.result.aiAuthored === true}
                      meta={
                          <>
                              <span>{said.kind(row.kind)}</span>
                              {row.project === undefined ? null : (
                                  <>
                                      <Dot />
                                      <button
                                          type="button"
                                          className="bilda-search__project"
                                          title={row.project.title}
                                          onClick={() =>
                                              row.project === undefined
                                                  ? undefined
                                                  : onOpenProject(
                                                        row.project.id,
                                                    )
                                          }
                                      >
                                          <Icon name="folder" size="xs" />
                                          {row.project.title}
                                      </button>
                                  </>
                              )}
                              {row.result.archived === true ? (
                                  <>
                                      <Dot />
                                      <span>{t`Archived`}</span>
                                  </>
                              ) : null}
                              <When
                                  text={said.changed(
                                      row.result.recency,
                                      date(row.result.editedAt),
                                  )}
                              />
                          </>
                      }
                      menu={actions.menu(row.result)}
                  />
              ))
            : sources.slice(0, shownCount).map((row) => (
                  <Row
                      key={row.result.id}
                      icon={fileKindIcon(row.fileKind)}
                      title={actions.title(row.result, words, () =>
                          onOpen(row.result),
                      )}
                      ai={false}
                      meta={
                          <>
                              <span>{fileKind(row.fileKind)}</span>
                              <Dot />
                              <SourceUse row={row} />
                              {row.result.archived === true ? (
                                  <>
                                      <Dot />
                                      <span>{t`Archived`}</span>
                                  </>
                              ) : null}
                              <When text={added(date(row.result.createdAt))} />
                          </>
                      }
                      menu={actions.menu(row.result)}
                  />
              ));

    return (
        <div className="bilda-search__list">
            <ul className="bilda-search__items">{items}</ul>
            {showArchived}
            {shownCount < rows ? (
                <div className="bilda-search__foot">
                    <p className="bilda-search__total">
                        {t`Showing ${shownCount} of ${rows} matches`}
                    </p>
                    <Button variant="secondary" size="md" onClick={onShowMore}>
                        {t`Show more`}
                        <Icon name="chevron-down" size="sm" />
                    </Button>
                </div>
            ) : null}
        </div>
    );
};

// Where a source stands: being read, could not be read, or how many
// projects use it.
const SourceUse = ({ row }: { readonly row: SourceRow }): ReactNode => {
    const { t } = useLingui();
    const said = useSearchWords();
    switch (row.state) {
        case "reading":
            return <span>{t`Being read…`}</span>;
        case "unreadable":
            return (
                <span className="bilda-search__danger">{t`Could not be read`}</span>
            );
        case "read":
            return row.usedIn.length === 0 ? (
                <span>{said.usedIn(0)}</span>
            ) : (
                <span
                    className="bilda-search__used"
                    title={row.usedIn.map((u) => u.title).join(", ")}
                >
                    <Icon name="folder" size="xs" />
                    {said.usedIn(row.usedIn.length)}
                </span>
            );
    }
};
